/*
  Cart Popup behaviour.
  Globals expected:
    window.cartPopupConfig        — bootstrapped by sections/cart-popup.liquid
    window.discount_percentage    — set by check-customer-on-PDP / -cart-page (may be undefined elsewhere)
    window.discount_percentage_multiplier
    window.calculate_displayed_price
    theme.Shopify.formatMoney, theme.money_format
    jQuery ($)

  Globals exposed (do not rename — used by:
    sections/main-product-vip.liquid:217    → openCartPopup
    sections/cart-page-listing.liquid       → updateCartPopupItemList, createCartPopupLineItem, updateHeaderCartIcon
  ):
    window.openCartPopup
    window.closeCartPopup
    window.updateCartPopupItemList
    window.createCartPopupLineItem
    window.updateHeaderCartIcon
*/

(function () {
  'use strict';

  var FOCUSABLE_SEL = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var QTY_DEBOUNCE_MS = 200;

  var lastTrigger    = null;
  var escapeHandler  = null;
  var tabHandler     = null;
  var qtyTimers      = {};           // variant_id -> setTimeout id
  var qtyPending     = {};           // variant_id -> next-quantity
  var crossSellSeed  = null;
  var crossSellCache = null;          // { seedId, items[] }
  var bound          = false;

  function $wrap()    { return document.querySelector('[data-cart-drawer]'); }
  function $drawer()  { return document.querySelector('.cart-popup'); }
  function $list()    { return document.querySelector('.cart-popup-list'); }
  function getMultiplier() {
    var m = parseFloat(window.discount_percentage_multiplier);
    return (window.calculate_displayed_price && !isNaN(m) && m > 0) ? m : 1;
  }
  function getDiscountPercent() {
    var p = parseInt(window.discount_percentage, 10);
    return isNaN(p) ? 0 : p;
  }
  function fmt(amount) {
    if (window.theme && theme.Shopify && typeof theme.Shopify.formatMoney === 'function') {
      return theme.Shopify.formatMoney(amount, theme.money_format);
    }
    return '£' + (amount / 100).toFixed(2);
  }
  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
  function attrEscape(s) { return escapeHtml(s); }

  /* ───────── Open / close lifecycle ───────── */

  function openCartPopup(triggerEl) {
    var wrap = $wrap();
    if (!wrap) return;
    lastTrigger = triggerEl || document.activeElement;
    wrap.removeAttribute('hidden');
    var main = document.getElementById('MainContent');
    if (main) main.setAttribute('inert', '');
    document.body.style.overflow = 'hidden';

    // double rAF so [hidden] removal commits before transitions fire
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        document.body.classList.add('cart-popup-open');
      });
    });

    updateCartPopupItemList(function () {
      var close = wrap.querySelector('.cart-popup-close');
      if (close) close.focus();
    });

    escapeHandler = function (e) { if (e.key === 'Escape' || e.keyCode === 27) closeCartPopup(); };
    tabHandler = function (e) {
      if (e.key !== 'Tab' && e.keyCode !== 9) return;
      var focusables = Array.prototype.filter.call(
        wrap.querySelectorAll(FOCUSABLE_SEL),
        function (el) { return el.offsetParent !== null; }
      );
      if (!focusables.length) return;
      var first = focusables[0];
      var last  = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', escapeHandler);
    wrap.addEventListener('keydown', tabHandler);
  }

  function closeCartPopup() {
    var wrap = $wrap();
    if (!wrap) return;
    document.body.classList.remove('cart-popup-open');
    var main = document.getElementById('MainContent');
    if (main) main.removeAttribute('inert');
    document.body.style.overflow = '';
    if (escapeHandler) document.removeEventListener('keydown', escapeHandler);
    if (tabHandler)    wrap.removeEventListener('keydown', tabHandler);
    setTimeout(function () { wrap.setAttribute('hidden', ''); }, 420);
    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      try { lastTrigger.focus(); } catch (e) {}
    }
  }

  /* ───────── AJAX: refresh full cart ───────── */

  function updateCartPopupItemList(callback) {
    $.getJSON('/cart.js', function (cart) {
      reconcileMultiplier(cart);
      renderLines(cart);
      updateCartPopupSubtotal(cart);
      updateCartPopupTiers(cart);
      updateHeaderCartIcon(cart);
      renderEmptyState(cart);
      loadCartPopupCrossSell(cart);
      if (typeof callback === 'function') callback();
    });
  }

  /*
    Reconcile the displayed multiplier against what Shopify actually charged.
    Source of truth: cart.total_price / cart.original_total_price when items
    exist and Shopify has applied an automatic discount. If the live ratio
    diverges from the tag-derived window.discount_percentage_multiplier
    (e.g. marketing edits the % in Admin without updating theme code), we
    overwrite the global and fire harpar:pricing-changed so any visible
    storefront price renderer can re-render in place.
  */
  function reconcileMultiplier(cart) {
    if (!cart || !cart.items || !cart.items.length) return;
    if (!cart.original_total_price || cart.original_total_price <= 0) return;
    if (cart.total_price === cart.original_total_price) return; // no discount applied yet
    var observed = cart.total_price / cart.original_total_price;
    if (!isFinite(observed) || observed <= 0 || observed > 1) return;
    var current = parseFloat(window.discount_percentage_multiplier);
    if (isNaN(current)) current = 1;
    // ignore tiny rounding noise (<= 0.005 = 0.5 percentage points)
    if (Math.abs(observed - current) < 0.005) return;
    window.discount_percentage_multiplier = observed;
    window.discount_percentage = Math.round((1 - observed) * 100);
    try {
      document.dispatchEvent(new CustomEvent('harpar:pricing-changed', {
        detail: { multiplier: observed, percent: window.discount_percentage }
      }));
    } catch (e) { /* IE — ignore */ }
  }

  function renderLines(cart) {
    var $ul = $('.cart-popup-list');
    if (!$ul.length) return;
    $ul.empty();
    for (var i = 0; i < cart.items.length; i++) {
      $ul.append(createCartPopupLineItem(cart.items[i]));
    }
  }

  function renderEmptyState(cart) {
    var d = $drawer();
    if (!d) return;
    d.setAttribute('data-state', cart.item_count > 0 ? 'active' : 'empty');
  }

  function updateHeaderCartIcon(cart) {
    $('.main-menu-right-col .buttons-wrapper .shopping-cart-wrapper a div span').html(cart.item_count);
  }

  /* ───────── Line item template (DRIFT-SYNCED with Liquid {% for %} in sections/cart-popup.liquid) ───────── */

  function createCartPopupLineItem(item) {
    var multiplier  = getMultiplier();
    var rrpUnit     = item.original_price != null ? item.original_price : item.price;
    var vipUnit     = item.final_price    != null ? item.final_price    : item.price;
    if (vipUnit === rrpUnit && multiplier < 1) vipUnit = Math.round(rrpUnit * multiplier);

    var variantTitle = (item.variant_title && item.variant_title !== 'Default Title') ? item.variant_title : '';
    var imgSrc = item.image ? item.image : '';
    var showRrp = rrpUnit > vipUnit;

    var html =
      '<li class="cart-popup-line" data-line-id="' + attrEscape(item.variant_id) + '">' +
        '<a class="cart-popup-line-image" href="' + attrEscape(item.url) + '" tabindex="-1" aria-hidden="true">' +
          (imgSrc ? '<img src="' + attrEscape(imgSrc) + '" alt="" loading="lazy" width="72" height="72">' : '') +
        '</a>' +
        '<div class="cart-popup-line-body">' +
          '<button class="cart-popup-line-remove" type="button" ' +
                  'data-id="' + attrEscape(item.variant_id) + '" ' +
                  'aria-label="Remove ' + attrEscape(item.product_title || item.title) + '">' +
            '<svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">' +
              '<path d="M2 2 L10 10 M10 2 L2 10" stroke="currentColor" stroke-width="1" fill="none"/>' +
            '</svg>' +
          '</button>' +
          '<a class="cart-popup-line-title" href="' + attrEscape(item.url) + '">' +
            escapeHtml(item.product_title || item.title) +
          '</a>' +
          (variantTitle ? '<p class="cart-popup-line-variant">' + escapeHtml(variantTitle) + '</p>' : '') +
          '<div class="cart-popup-line-foot">' +
            '<div class="cart-popup-line-prices">' +
              (showRrp ? '<span class="cart-popup-line-rrp">' + fmt(rrpUnit) + '</span>' : '') +
              '<span class="cart-popup-line-vip">' + fmt(vipUnit) + '</span>' +
            '</div>' +
            '<div class="cart-popup-line-qty" data-loading="false">' +
              '<button data-op="minus" data-id="' + attrEscape(item.variant_id) + '" type="button" aria-label="Decrease quantity">' +
                '<svg viewBox="0 0 8 1" width="8" height="1" aria-hidden="true">' +
                  '<line x1="0" y1="0.5" x2="8" y2="0.5" stroke="currentColor" stroke-width="1"/>' +
                '</svg>' +
              '</button>' +
              '<input type="number" min="0" inputmode="numeric" name="quantity" ' +
                     'value="' + attrEscape(item.quantity) + '" data-id="' + attrEscape(item.variant_id) + '" ' +
                     'aria-label="Quantity for ' + attrEscape(item.product_title || item.title) + '">' +
              '<button data-op="plus" data-id="' + attrEscape(item.variant_id) + '" type="button" aria-label="Increase quantity">' +
                '<svg viewBox="0 0 8 8" width="8" height="8" aria-hidden="true">' +
                  '<line x1="0" y1="4" x2="8" y2="4" stroke="currentColor" stroke-width="1"/>' +
                  '<line x1="4" y1="0" x2="4" y2="8" stroke="currentColor" stroke-width="1"/>' +
                '</svg>' +
              '</button>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</li>';
    return $(html);
  }

  /* ───────── Subtotal (writes to BOTH drawer and cart-page surfaces) ───────── */

  function updateCartPopupSubtotal(cart) {
    var multiplier = getMultiplier();
    var original   = cart.original_total_price;
    var total      = cart.total_price;
    if (total === original && multiplier < 1) total = Math.round(original * multiplier);
    var saved = Math.max(0, original - total);
    var pct   = getDiscountPercent();

    var html;
    if (saved > 0) {
      html =
        '<div class="row">' +
          '<span class="row-label">Subtotal</span>' +
          '<span class="row-value">' + fmt(original) + '</span>' +
        '</div>' +
        '<div class="row row--savings">' +
          '<span class="row-label">VIP price' + (pct > 0 ? ' (' + pct + '% off)' : '') + '</span>' +
          '<span class="row-value">−' + fmt(saved) + '</span>' +
        '</div>' +
        '<div class="row row--total">' +
          '<span class="row-label">Total</span>' +
          '<span class="row-value">' + fmt(total) + '</span>' +
        '</div>';
    } else {
      html =
        '<div class="row row--total">' +
          '<span class="row-label">Total</span>' +
          '<span class="row-value">' + fmt(total) + '</span>' +
        '</div>';
    }
    $('.cart-popup-subtotal-price').html(html);
    $('.cart-page-subtotal-price').html(html);
  }

  /* ───────── Tiered free-shipping progress ───────── */

  function getTier() {
    var cfg = window.cartPopupConfig || {};
    var tiers = (cfg.tiers || []).filter(function (t) { return t && t.threshold > 0; });
    return tiers[0] || null;
  }

  function updateCartPopupTiers(cart) {
    var tier = getTier();
    var box = document.querySelector('.cart-popup-tiers');
    if (!box) return;
    if (!tier) { box.style.display = 'none'; return; }
    box.style.display = '';

    var multiplier = getMultiplier();
    var total = cart.total_price;
    if (total === cart.original_total_price && multiplier < 1) {
      total = Math.round(cart.original_total_price * multiplier);
    }

    var label = tier.label || 'free delivery';
    var fill, text;
    if (cart.item_count === 0) {
      fill = 0;
      text = 'Free delivery on orders over ' + fmt(tier.threshold) + '.';
    } else if (total >= tier.threshold) {
      fill = 100;
      text = capitalise(label) + ' unlocked.';
    } else {
      var remaining = tier.threshold - total;
      fill = Math.max(0, Math.min(100, (total / tier.threshold) * 100));
      text = (remaining <= 500)
        ? 'Almost there — ' + fmt(remaining) + ' to go for ' + label + '.'
        : 'Add ' + fmt(remaining) + ' more for ' + label + '.';
    }

    var fillEl = box.querySelector('.cart-popup-progress-fill');
    if (fillEl) fillEl.style.width = fill + '%';
    var bar = box.querySelector('.cart-popup-progress');
    if (bar) bar.setAttribute('aria-valuenow', String(Math.round(fill)));
    var p = box.querySelector('.cart-popup-tier-text');
    if (p) p.textContent = text;
  }

  function capitalise(s) {
    if (!s) return s;
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  /* ───────── Cross-sell ───────── */

  function loadCartPopupCrossSell(cart) {
    var cfg = (window.cartPopupConfig && window.cartPopupConfig.cross_sell) || {};
    var box = document.querySelector('[data-cross-sell]');
    if (!box) return;
    if (!cfg.enabled || !cart.items.length) {
      box.setAttribute('hidden', '');
      return;
    }
    var seed = cart.items[0].product_id;
    var limit = (cfg.limit && cfg.limit > 0) ? cfg.limit : 3;
    // expand limit so post-filter we still have enough
    var fetchLimit = Math.min(10, limit + cart.items.length);

    if (crossSellCache && crossSellCache.seedId === seed) {
      renderCrossSell(crossSellCache.items, cart, limit);
      return;
    }

    var url = '/recommendations/products.json?product_id=' + encodeURIComponent(seed) +
              '&limit=' + fetchLimit + '&intent=related';
    $.getJSON(url)
      .done(function (data) {
        var products = (data && data.products) ? data.products : [];
        crossSellCache = { seedId: seed, items: products };
        renderCrossSell(products, cart, limit);
      })
      .fail(function () {
        box.setAttribute('hidden', '');
      });
  }

  function renderCrossSell(products, cart, limit) {
    var box = document.querySelector('[data-cross-sell]');
    var ul  = box && box.querySelector('.cart-popup-cross-sell-list');
    if (!box || !ul) return;
    var inCart = {};
    for (var i = 0; i < cart.items.length; i++) inCart[cart.items[i].product_id] = true;

    var filtered = products.filter(function (p) { return !inCart[p.id]; }).slice(0, limit);
    if (!filtered.length) { box.setAttribute('hidden', ''); return; }

    var multiplier = getMultiplier();
    var html = '';
    for (var j = 0; j < filtered.length; j++) {
      var p = filtered[j];
      var firstVariant = (p.variants && p.variants[0]) ? p.variants[0] : null;
      if (!firstVariant) continue;
      var available = firstVariant.available !== false;
      var rrp = (p.price != null) ? p.price : firstVariant.price;
      var vip = Math.round(rrp * multiplier);
      var showRrp = vip < rrp;
      var img = (p.featured_image && (p.featured_image.url || p.featured_image)) || p.image || '';
      if (typeof img === 'object' && img !== null) img = img.url || '';
      var href = attrEscape(p.url || ('/products/' + p.handle));
      var title = escapeHtml(p.title);

      html +=
        '<li class="cart-popup-cross-sell-item">' +
          '<a class="cart-popup-cross-sell-image" href="' + href + '" tabindex="-1" aria-hidden="true">' +
            (img ? '<img src="' + attrEscape(img) + '" alt="" loading="lazy">' : '') +
          '</a>' +
          '<a class="cart-popup-cross-sell-title" href="' + href + '">' + title + '</a>' +
          '<div class="cart-popup-cross-sell-prices">' +
            (showRrp ? '<span class="cart-popup-cross-sell-rrp">' + fmt(rrp) + '</span>' : '') +
            '<span class="cart-popup-cross-sell-vip">' + fmt(vip) + '</span>' +
          '</div>' +
          (available
            ? '<button class="cart-popup-cross-sell-add" type="button" data-variant-id="' + attrEscape(firstVariant.id) + '" data-loading="false" aria-label="Add ' + attrEscape(p.title) + ' to cart">Add</button>'
            : '<span class="cart-popup-cross-sell-soldout">Sold out</span>') +
        '</li>';
    }
    ul.innerHTML = html;
    box.removeAttribute('hidden');
    // reset scroll on rebuild so the first card is visible
    ul.scrollLeft = 0;
  }

  /* ───────── Delegated event bindings (once) ───────── */

  function bindDelegated() {
    if (bound) return;
    bound = true;

    var $body = $(document.body);

    // Cart icon → open
    $body.on('click', '.main-menu-right-col .buttons-wrapper .shopping-cart-wrapper a', function (e) {
      e.preventDefault();
      openCartPopup(this);
      return false;
    });

    // Close: overlay, ×, continue-shopping
    $body.on('click', '[data-cart-drawer] [data-overlay], [data-cart-drawer] .cart-popup-close', function (e) {
      e.preventDefault();
      closeCartPopup();
    });
    $body.on('click', '[data-cart-drawer] .cart-popup-continue', function (e) {
      // allow real href as fallback if JS is disabled; intercept to close
      e.preventDefault();
      closeCartPopup();
    });

    // Qty steppers
    $body.on('click', '.cart-popup-line-qty button', function (e) {
      e.preventDefault();
      var btn = this;
      var variantId = $(btn).data('id');
      var $row = $(btn).closest('.cart-popup-line-qty');
      var $input = $row.find('input');
      var current = parseInt($input.val(), 10);
      if (isNaN(current)) current = 0;
      var next = $(btn).data('op') === 'plus' ? current + 1 : Math.max(0, current - 1);
      $input.val(next);
      $row.attr('data-loading', 'true');
      scheduleQtyChange(variantId, next);
    });
    $body.on('change', '.cart-popup-line-qty input', function () {
      var $input = $(this);
      var variantId = $input.data('id');
      var quantity = parseInt($input.val(), 10);
      if (isNaN(quantity) || quantity < 0) quantity = 0;
      $input.closest('.cart-popup-line-qty').attr('data-loading', 'true');
      scheduleQtyChange(variantId, quantity);
    });

    // Remove
    $body.on('click', '.cart-popup-line-remove', function (e) {
      e.preventDefault();
      var variantId = $(this).data('id');
      var $line = $(this).closest('.cart-popup-line');
      $line.css('max-height', $line.outerHeight() + 'px');
      // force reflow then collapse
      $line[0].offsetHeight; // eslint-disable-line no-unused-expressions
      $line.addClass('cart-popup-line--removing');
      setTimeout(function () {
        $.post('/cart/change.json', { id: variantId, quantity: 0 }, function () {
          updateCartPopupItemList();
        });
      }, 200);
    });

    // Cross-sell add
    $body.on('click', '.cart-popup-cross-sell-add', function (e) {
      e.preventDefault();
      var btn = this;
      var variantId = $(btn).data('variant-id');
      $(btn).attr('data-loading', 'true');
      $.ajax({
        url: '/cart/add.js',
        method: 'POST',
        dataType: 'json',
        data: { id: variantId, quantity: 1 }
      })
      .done(function () { updateCartPopupItemList(); })
      .always(function () { $(btn).attr('data-loading', 'false'); });
    });

    // External callers
    window.openCartPopup           = openCartPopup;
    window.closeCartPopup          = closeCartPopup;
    window.updateCartPopupItemList = updateCartPopupItemList;
    window.createCartPopupLineItem = createCartPopupLineItem;
    window.updateHeaderCartIcon    = updateHeaderCartIcon;
  }

  function scheduleQtyChange(variantId, quantity) {
    qtyPending[variantId] = quantity;
    if (qtyTimers[variantId]) clearTimeout(qtyTimers[variantId]);
    qtyTimers[variantId] = setTimeout(function () {
      var qty = qtyPending[variantId];
      delete qtyPending[variantId];
      delete qtyTimers[variantId];
      $.post('/cart/change.json', { id: variantId, quantity: qty }, function () {
        updateCartPopupItemList();
      });
    }, QTY_DEBOUNCE_MS);
  }

  /* ───────── Init ───────── */

  function init() {
    if (typeof window.jQuery === 'undefined') {
      // jQuery is loaded by the layout; if it's not here yet, wait briefly.
      setTimeout(init, 50);
      return;
    }
    bindDelegated();
    // Refresh header count + render lines on first load (covers SSR ≠ fresh cart cases)
    if (document.querySelector('[data-cart-drawer]')) {
      $.getJSON('/cart.js', function (cart) {
        updateHeaderCartIcon(cart);
        renderEmptyState(cart);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
